import React, { useEffect, useState, useCallback } from "react";
import { api, errMsg, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, Settings, RefreshCw, Plus, Trash2, Coins } from "lucide-react";

function Card({ title, icon: Icon, children }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 mb-5">
      <div className="flex items-center gap-2 mb-4">
        {Icon && <Icon className="h-5 w-5 text-[#F47920]" />}
        <h2 className="font-head font-semibold text-slate-900">{title}</h2>
      </div>
      {children}
    </div>
  );
}

export default function Configuration() {
  const { user } = useAuth();
  const canManage = ["admin", "executive"].includes(user?.role);
  const canFx = ["admin", "finance", "executive"].includes(user?.role);
  const [settings, setSettings] = useState(null);
  const [fx, setFx] = useState(null);
  const [manual, setManual] = useState({ currency: "", rate: "" });
  const [tax, setTax] = useState({ name: "", rate: "", applies_to: "All" });
  const [conv, setConv] = useState({ from: "USD", to: "EUR", amount: 1000 });
  const [convRes, setConvRes] = useState(null);

  const loadSettings = useCallback(() => api.get("/settings").then(({ data }) => setSettings(data)).catch(() => {}), []);
  const loadFx = useCallback(() => api.get("/fx/rates").then(({ data }) => setFx(data)).catch(() => {}), []);
  useEffect(() => { loadSettings(); loadFx(); }, [loadSettings, loadFx]);

  const saveSettings = async () => {
    try { const { data } = await api.put("/settings", settings); setSettings(data); toast.success("Settings saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const refreshFx = async () => {
    try { const { data } = await api.post("/fx/refresh"); setFx(data); toast.success("Live FX rates refreshed"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const addManual = async () => {
    if (!manual.currency || !manual.rate) { toast.error("Currency and rate required"); return; }
    try { const { data } = await api.post("/fx/manual", { currency: manual.currency, rate: Number(manual.rate) }); setFx(data); setManual({ currency: "", rate: "" }); toast.success("Manual rate set"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delManual = async (ccy) => {
    try { const { data } = await api.delete(`/fx/manual/${ccy}`); setFx(data); } catch (e) { toast.error(errMsg(e)); }
  };
  const addTax = async () => {
    if (!tax.name || tax.rate === "") { toast.error("Name and rate required"); return; }
    try { await api.post("/tax/rules", { name: tax.name, rate: Number(tax.rate), applies_to: tax.applies_to }); setTax({ name: "", rate: "", applies_to: "All" }); loadSettings(); toast.success("Tax rule added"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const delTax = async (id) => { try { await api.delete(`/tax/rules/${id}`); loadSettings(); } catch (e) { toast.error(errMsg(e)); } };
  const convert = async () => {
    try { const { data } = await api.get(`/fx/convert?from=${conv.from}&to=${conv.to}&amount=${conv.amount}`); setConvRes(data); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!settings || !fx) return <div className="py-20 text-center text-slate-400"><Loader2 className="h-6 w-6 animate-spin inline" /></div>;
  const num = (k) => (
    <div className="grid gap-1.5">
      <Label className="text-xs text-slate-600">{k.label}</Label>
      <Input type="number" disabled={!canManage} value={settings[k.key] ?? ""} data-testid={`setting-${k.key}`}
        onChange={(e) => setSettings((s) => ({ ...s, [k.key]: e.target.value }))} />
    </div>
  );
  const manualEntries = Object.entries(fx.manual_rates || {});
  const topRates = ["USD", "EUR", "GBP", "NGN", "GHS", "XOF", "ZAR", "CNY"].filter((c) => fx.rates[c] != null);

  return (
    <div>
      <div className="mb-5">
        <h1 className="font-head text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2"><Settings className="h-6 w-6 text-[#12346B]" /> Configuration</h1>
        <p className="text-sm text-slate-500 mt-0.5">Workflow thresholds, tolerances, multi-currency FX and tax rules.</p>
      </div>

      <Card title="Workflow Thresholds & Tolerances" icon={Settings}>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { key: "qty_tolerance_pct", label: "Quantity Tolerance (%)" },
            { key: "price_tolerance_pct", label: "Price Tolerance (%)" },
            { key: "financial_tolerance_pct", label: "Financial Tolerance (%)" },
            { key: "so_approval_threshold", label: "Sales Order Approval Threshold (USD)" },
            { key: "payment_approval_threshold", label: "Payment Approval Threshold (USD)" },
            { key: "default_tax_rate", label: "Default Tax Rate (%)" },
            { key: "margin_floor_pct", label: "Minimum Margin Floor (%)" },
          ].map((k) => <div key={k.key}>{num(k)}</div>)}
          <div className="grid gap-1.5">
            <Label className="text-xs text-slate-600">Base Currency</Label>
            <Input disabled={!canManage} value={settings.base_currency || "USD"} data-testid="setting-base_currency"
              onChange={(e) => setSettings((s) => ({ ...s, base_currency: e.target.value.toUpperCase() }))} />
          </div>
        </div>
        {canManage && <Button className="mt-4 bg-[#12346B] hover:bg-[#12346B]/90" onClick={saveSettings} data-testid="btn-save-settings">Save Settings</Button>}
      </Card>

      <Card title="Multi-Currency FX Rates" icon={Coins}>
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-slate-500">Base {fx.base} · Source: {fx.source} {fx.updated_at ? `· updated ${new Date(fx.updated_at).toLocaleString()}` : ""}</p>
          {canFx && <Button size="sm" variant="outline" onClick={refreshFx} data-testid="btn-fx-refresh"><RefreshCw className="h-4 w-4 mr-1" /> Refresh Live Rates</Button>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
          {topRates.map((c) => (
            <div key={c} className="border border-slate-200 rounded-md px-3 py-2 text-sm flex justify-between" data-testid={`fx-rate-${c}`}>
              <span className="font-semibold text-slate-700">{c}</span>
              <span className="tabular text-slate-500">{fmtNum(fx.rates[c], 4)}</span>
            </div>
          ))}
        </div>
        {canFx && (
          <div className="flex flex-wrap items-end gap-3 border-t border-slate-100 pt-4">
            <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Currency</Label><Input className="w-28 uppercase" placeholder="EUR" value={manual.currency} onChange={(e) => setManual((s) => ({ ...s, currency: e.target.value.toUpperCase() }))} data-testid="fx-manual-ccy" /></div>
            <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Manual Rate (per 1 USD)</Label><Input className="w-40" type="number" value={manual.rate} onChange={(e) => setManual((s) => ({ ...s, rate: e.target.value }))} data-testid="fx-manual-rate" /></div>
            <Button variant="outline" onClick={addManual} data-testid="btn-fx-manual"><Plus className="h-4 w-4 mr-1" /> Set Override</Button>
          </div>
        )}
        {manualEntries.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {manualEntries.map(([c, r]) => (
              <span key={c} className="inline-flex items-center gap-2 bg-orange-50 border border-orange-200 rounded-full px-3 py-1 text-xs text-orange-700" data-testid={`fx-manual-tag-${c}`}>
                {c}: {fmtNum(r, 4)} (manual)
                {canFx && <button onClick={() => delManual(c)} className="text-orange-400 hover:text-orange-700"><Trash2 className="h-3 w-3" /></button>}
              </span>
            ))}
          </div>
        )}
        <div className="mt-4 border-t border-slate-100 pt-4 flex flex-wrap items-end gap-3">
          <div className="grid gap-1.5"><Label className="text-xs text-slate-600">From</Label><Input className="w-24 uppercase" value={conv.from} onChange={(e) => setConv((s) => ({ ...s, from: e.target.value.toUpperCase() }))} data-testid="conv-from" /></div>
          <div className="grid gap-1.5"><Label className="text-xs text-slate-600">To</Label><Input className="w-24 uppercase" value={conv.to} onChange={(e) => setConv((s) => ({ ...s, to: e.target.value.toUpperCase() }))} data-testid="conv-to" /></div>
          <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Amount</Label><Input className="w-32" type="number" value={conv.amount} onChange={(e) => setConv((s) => ({ ...s, amount: e.target.value }))} data-testid="conv-amount" /></div>
          <Button variant="outline" onClick={convert} data-testid="btn-convert">Convert</Button>
          {convRes && <span className="text-sm font-semibold text-[#12346B]" data-testid="conv-result">{fmtNum(convRes.amount, 2)} {convRes.from} = {fmtNum(convRes.result, 2)} {convRes.to}</span>}
        </div>
      </Card>

      <Card title="Tax Rules" icon={Coins}>
        <Table className="pf-table mb-3">
          <TableHeader><TableRow className="bg-slate-50 hover:bg-slate-50">
            {["Name", "Rate (%)", "Applies To", ""].map((h) => <TableHead key={h} className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold py-2 px-3">{h}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {(settings.tax_rules || []).length === 0 ? <TableRow><TableCell colSpan={4} className="py-6 text-center text-slate-400 text-sm">No tax rules configured.</TableCell></TableRow>
              : settings.tax_rules.map((r) => (
                <TableRow key={r.id} data-testid={`tax-rule-${r.id}`}><TableCell className="py-2 px-3 text-sm">{r.name}</TableCell>
                  <TableCell className="py-2 px-3 text-sm tabular">{r.rate}</TableCell><TableCell className="py-2 px-3 text-sm">{r.applies_to}</TableCell>
                  <TableCell className="py-2 px-3 text-right">{canManage && <button onClick={() => delTax(r.id)} className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}</TableCell></TableRow>
              ))}
          </TableBody>
        </Table>
        {canManage && (
          <div className="flex flex-wrap items-end gap-3">
            <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Rule Name</Label><Input className="w-48" placeholder="Import Duty" value={tax.name} onChange={(e) => setTax((s) => ({ ...s, name: e.target.value }))} data-testid="tax-name" /></div>
            <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Rate (%)</Label><Input className="w-28" type="number" value={tax.rate} onChange={(e) => setTax((s) => ({ ...s, rate: e.target.value }))} data-testid="tax-rate" /></div>
            <div className="grid gap-1.5"><Label className="text-xs text-slate-600">Applies To</Label><Input className="w-40" value={tax.applies_to} onChange={(e) => setTax((s) => ({ ...s, applies_to: e.target.value }))} data-testid="tax-applies" /></div>
            <Button variant="outline" onClick={addTax} data-testid="btn-add-tax"><Plus className="h-4 w-4 mr-1" /> Add Rule</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
